import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "../../i18n";
import { fetchJsonFromApiCandidates } from "../../utils/api";
import { isFallbackBlog } from "../../utils/fallbackBlogs";
import "./BlogPostDetails.css";

function BlogPostDetails({ title, content, summary, date, postId, initialComments = [], isStaticPost = false }) {
    const { language, t } = useTranslation();
    const [comments, setComments] = useState(Array.isArray(initialComments) ? initialComments : []);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [newComment, setNewComment] = useState("");

    const fetchComments = useCallback(async () => {
        if (!postId || isStaticPost || isFallbackBlog(postId)) return;
    
        try {
            const { data } = await fetchJsonFromApiCandidates(`/api/blogs/${postId}/comments`);
            setComments(data);
        } catch (error) {
            console.error("Error al cargar comentarios:", error);
        }
    }, [isStaticPost, postId]);

    useEffect(() => {
        if (postId && !isStaticPost) {
            fetchComments();
        }
    }, [fetchComments, isStaticPost, postId]);

    const handleAddComment = async () => {
        if (!name.trim() || !email.trim() || !newComment.trim()) return;

        if (isStaticPost || isFallbackBlog(postId)) {
            setComments((currentComments) => [
                ...currentComments,
                { name, email, comment: newComment }
            ]);
            setName("");
            setEmail("");
            setNewComment("");
            return;
        }

        try {
            const { baseUrl } = await fetchJsonFromApiCandidates(`/api/blogs/${postId}/comment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, comment: newComment })
            });

            if (baseUrl) {
                fetchComments(); // Recargar comentarios después de enviar
                setName("");
                setEmail("");
                setNewComment("");
            }
        } catch (error) {
            console.error("Error al añadir comentario:", error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const options = { day: "2-digit", month: "long", year: "numeric" };
        return new Date(dateString).toLocaleDateString(language === "de" ? "de-DE" : language === "en" ? "en-US" : "es-ES", options);
    };

    return (
        <article id="blogpost-posts" className="blogpost-container">
            <header className="blogpost-header">
                <h1 className="blogpost-title">{title}</h1>
                <p className="blogpost-date">
                    <span className="theqahub-font">{"</"}TheQAHub{">"}</span> | 📅 {formatDate(date)} | {t("blogPost.section")}
                </p>
            </header>
            
            <h2 className="blogpost-summary">{summary}</h2>
            
            <section className="blogpost-content">
                <div
                    dangerouslySetInnerHTML={{
                        __html: content
                            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // **texto** => <strong>texto</strong>
                            .replace(/\n/g, "<br>") // saltos de línea a <br>
                    }}
                />
            </section>

            {/* Sección de Comentarios */}
            <section className="comments-section">
                <h3>{t("blogPost.comments")} ({comments.length})</h3>
                <div className="comment-box">
                    <input 
                        type="text" 
                        placeholder={t("blog.namePlaceholder")} 
                        value={name} 
                        onChange={(e) => setName(e.target.value)} 
                    />
                    <input 
                        type="email" 
                        placeholder={t("blog.emailPlaceholder")} 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                    />
                    <input 
                        type="text" 
                        placeholder={t("blog.commentPlaceholder")} 
                        value={newComment} 
                        onChange={(e) => setNewComment(e.target.value)} 
                    />
                    <button onClick={handleAddComment}>{t("common.send")}</button>
                </div>

                <div className="comments-list">
                    {comments.length > 0 ? (
                        comments.map((comment, index) => (
                            <div key={index} className="comment">
                                <p><strong>{comment.name} ({comment.email}):</strong> {comment.comment}</p>
                            </div>
                        ))
                    ) : (
                        <p className="no-comments">{t("blog.noComments")}</p>
                    )}
                </div>
            </section>
        </article>
    );
}

export default BlogPostDetails;
